import clsx from 'clsx';
import {
  forwardRef,
  type HTMLAttributes,
  type RefObject,
  useLayoutEffect,
  useRef,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFontsUpdated } from '../../core/hooks/useFontsUpdated.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { centerEllipsizeText, type Font } from '../utils.js';
import * as styles from './TextEllipsis.sty.js';

const COMPONENT_NAME = 'TextEllipsis';

/**
 * Available modes for how overly long text is truncated and where the ellipsis is placed.
 * @public
 */
export type TruncationMode = 'start' | 'middle' | 'end';

/**
 * The props for the TextEllipsis component.
 * @public
 */
export interface TextEllipsisProps
  extends HTMLAttributes<HTMLSpanElement>,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /** The children (as text) passed to the component. */
  children: string;
  /**
   * The mode used for truncating the text, either at the start, in the middle or at the end.
   * @defaultValue 'end'
   */
  truncationMode?: TruncationMode;
  /** Gets called when text needs to get truncated due to lack of horizontal space. */
  onTextOverflow?: (ellipsized: boolean) => void;
}

/**
 * Returns an object containing all font-related CSS properties from
 * a given DOM element that are relevant for rendering formatted text
 * in a 2D canvas context.
 *
 * @param innerSpanElement - The HTML element to extract the CSS properties from
 */
function getFontProps(innerSpanElement: HTMLSpanElement): Font {
  const computedStyle = window.getComputedStyle(innerSpanElement, null);

  return {
    fontStyle: computedStyle.getPropertyValue('font-style'),
    fontWeight: computedStyle.getPropertyValue('font-weight'),
    fontSize: computedStyle.getPropertyValue('font-size'),
    fontFamily: computedStyle.getPropertyValue('font-family'),
  };
}

/**
 * Determines whether a CSS text-ellipsis is currently rendered in a container.
 * Note: `containerWidth` is only passed to avoid unnecessary reflows by calling `getBoundingClientRect()`
 */
function isNativeEllipsisActive(
  innerSpanElement: HTMLSpanElement,
  containerWidth: number,
): boolean {
  // Sometimes there already is an ellipsis, which means `getBoundingClientRect().width` and `containerWidth` are the same.
  // In that case, `scrollWidth` specifies the actual width of the text if it was fully visible.
  const originalTextWidth = Math.max(
    innerSpanElement.getBoundingClientRect().width,
    innerSpanElement.scrollWidth,
  );
  const textWidth = Math.floor(originalTextWidth * 100) / 100;

  return textWidth > containerWidth;
}

function resetFullText(
  innerSpanElement: HTMLSpanElement,
  originalText: string,
) {
  if (innerSpanElement.textContent !== originalText) {
    innerSpanElement.textContent = originalText;
  }
}

function getWidth(element: HTMLSpanElement) {
  return Math.max(element.getBoundingClientRect().width, element.clientWidth);
}

function getContainerWidth(containerElement: HTMLSpanElement) {
  return Math.ceil(getWidth(containerElement) * 100) / 100;
}

function onTextOverflowCallback(
  current: boolean,
  wasEllipsized: boolean,
  onTextOverflow: ((ellipsized: boolean) => void) | undefined,
) {
  if (onTextOverflow && current !== wasEllipsized) {
    onTextOverflow(current);
  }
}

/**
 * Detects whether a given formatted text has enough space within its
 * parent container and applies or removes an ellipsis accordingly.
 *
 * @param originalText - The text to be truncated if its width exceeds the container width
 * @param truncationMode - The mode used for truncating the text, either at the start, in the middle or at the end
 * @param containerRef - Object ref pointing towards the root element of the TextEllipsis component
 * @param wasEllipsized - Whether an ellipsis was applied during the last render cycle
 * @param onTextOverflow - Callback that gets called whenever the state of the ellipsis changes since the last render cycle
 *
 * @returns true, if the given text was ellipsized, false otherwise.
 */
function renderEllipsizedText(
  originalText: string,
  truncationMode: TextEllipsisProps['truncationMode'],
  containerRef: RefObject<HTMLSpanElement | null>,
  wasEllipsized: boolean,
  onTextOverflow?: (ellipsized: boolean) => void,
): boolean {
  const containerElement = containerRef.current;

  const innerSpanElement: HTMLSpanElement | null =
    (containerElement?.firstElementChild as HTMLSpanElement) || null;

  if (containerElement === null || innerSpanElement === null) {
    return false;
  }

  // always reset to full text before measuring container width
  resetFullText(innerSpanElement, originalText);
  const containerWidth = getContainerWidth(containerElement);

  if (!isNativeEllipsisActive(innerSpanElement, containerWidth)) {
    onTextOverflowCallback(false, wasEllipsized, onTextOverflow);
    return false;
  }

  if (truncationMode === 'middle') {
    innerSpanElement.textContent = centerEllipsizeText(
      originalText,
      getFontProps(innerSpanElement),
      containerWidth,
    );
  }

  onTextOverflowCallback(true, wasEllipsized, onTextOverflow);
  return true;
}

const ComputedTextEllipsis = /* @__PURE__ */ forwardRef<
  HTMLSpanElement,
  TextEllipsisProps
>((props, forwardedRef) => {
  const {
    children,
    truncationMode = 'end',
    onTextOverflow,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
  const { 'aria-label': ariaLabel, ...remainingAriaLabelingProps } =
    ariaLabelingProps;

  // resize observer is needed to re-calculate the ellipsis in case of resizing
  const { ref: observerContainerRef, width } =
    useResizeObserver<HTMLSpanElement>();
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const ellipsisStateRef = useRef<boolean>(false);
  const mergedRef = useMergeRefs<HTMLSpanElement | null>([
    observerContainerRef,
    (instance: HTMLSpanElement) => {
      containerRef.current = instance;
    },
    forwardedRef,
  ]);

  const fontsUpdated = useFontsUpdated();

  useLayoutEffect(() => {
    ellipsisStateRef.current = renderEllipsizedText(
      children,
      truncationMode,
      containerRef,
      ellipsisStateRef.current,
      onTextOverflow,
    );
  }, [width, children, truncationMode, fontsUpdated, onTextOverflow]);

  return (
    <span
      ref={mergedRef}
      aria-label={ariaLabel ?? children}
      className={clsx(
        styles.ellipsis({ truncationMode }),
        {
          [styles.active]: ellipsisStateRef.current,
        },
        consumerClassName,
      )}
      style={consumerStyle}
      data-sourcetext={children}
      data-ellipsis
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...remainingAriaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      <span>{children}</span>
    </span>
  );
});

const CssTextEllipsis = /* @__PURE__ */ forwardRef<
  HTMLSpanElement,
  TextEllipsisProps
>((props, forwardedRef) => {
  const {
    children,
    truncationMode = 'end',
    onTextOverflow,
    className: consumerClassName,
    style: consumerStyle,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
  const { 'aria-label': ariaLabel, ...remainingAriaLabelingProps } =
    ariaLabelingProps;

  return (
    <span
      ref={forwardedRef}
      aria-label={ariaLabel ?? children}
      data-ellipsis
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      className={clsx(styles.ellipsis({ truncationMode }), consumerClassName)}
      style={consumerStyle}
      {...remainingAriaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      <span>{children}</span>
    </span>
  );
});

/**
 * Use the `TextEllipsis` component to truncate text and show an ellipsis whenever
 * there is insufficient space to render the entire text. While some of our Strato
 * components provide ellipsis out of the box, you need to take care of this yourself
 * when writing your own components.
 * @public
 */
export const TextEllipsis = /* @__PURE__ */ forwardRef<
  HTMLSpanElement,
  TextEllipsisProps
>((props, forwardedRef) => {
  // in case onTextOverflow is not defined and the ellipsis is start or end
  // we can use CSS only which is significantly more performant
  const cssOnlyEllipsis =
    !props.onTextOverflow && props.truncationMode !== 'middle';

  return cssOnlyEllipsis ? (
    <CssTextEllipsis ref={forwardedRef} {...props} />
  ) : (
    <ComputedTextEllipsis ref={forwardedRef} {...props} />
  );
});

(TextEllipsis as typeof TextEllipsis & { displayName: string }).displayName =
  COMPONENT_NAME;
